import React, { useEffect } from 'react';
import EligibilityEnginePageView from '../views/EligibilityEnginePage/Index';
import NotFound from '../views/NotFound';
import useFeatureFlag from '../hooks/useFeatureFlag';
import { DB_ELIGIBILITY_ENGINE } from '../constants/featureFlags';
import {
  useEligibilityProducts,
  useEligibilityActions,
  useEligibilityTheme,
  useEligibilityRules,
  useHasUnsavedChanges,
  useResetEligibilityOnExit,
} from '../hooks/useEligibilityEngine';

const EligibilityEnginePageContent = () => {
  const {
    productsLoading, productsError, products, selectedProduct,
  } = useEligibilityProducts();
  const {
    fetchProducts,
    fetchRules,
    onRevert,
    onPublish,
    onProductChange,
    onIfNullChange,
    upsertNote,
    onAddRule,
    onRemoveRule,
  } = useEligibilityActions();
  const { productType, selectedProductData, themeClass } = useEligibilityTheme();
  const { rules, isRulesLoading, rulesError } = useEligibilityRules();
  const hasUnsavedChanges = useHasUnsavedChanges();

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch rules whenever the selected product changes
  useEffect(() => {
    if (selectedProduct) {
      fetchRules(selectedProduct);
    }
  }, [selectedProduct, fetchRules]);

  if (productsLoading) {
    return <div>Loading products...</div>;
  }

  if (productsError) {
    return <div>Error loading products. Please try again.</div>;
  }

  return (
    <EligibilityEnginePageView
      themeClass={themeClass}
      rules={rules}
      isRulesLoading={isRulesLoading}
      rulesError={rulesError}
      products={products}
      selectedProduct={selectedProduct}
      selectedProductData={selectedProductData}
      productType={productType}
      hasUnsavedChanges={hasUnsavedChanges}
      onRevert={onRevert}
      onPublish={onPublish}
      fetchRules={fetchRules}
      onProductChange={onProductChange}
      onIfNullChange={onIfNullChange}
      upsertNote={upsertNote}
      onAddRule={onAddRule}
      onRemoveRule={onRemoveRule}
    />
  );
};

// Gate the page on the DB feature flag: null while flags load, NotFound when
// disabled, otherwise the real page. Access is enforced by the authorize() HOC
// wrapping this component in App.js.
const EligibilityEnginePage = () => {
  const [isEligibilityEngineEnabled, loaded] = useFeatureFlag(
    DB_ELIGIBILITY_ENGINE
  );
  // Registered above the gates below so the cleanup exists even while flags load.
  useResetEligibilityOnExit();

  if (!loaded) return null;
  if (!isEligibilityEngineEnabled) return <NotFound />;

  return <EligibilityEnginePageContent />;
};

export default EligibilityEnginePage;
